import { orderBy, sortBy } from 'lodash';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    DropdownMultiSelectFilter,
    Enums,
    errorRedirect,
    humanizeString,
    inject,
    ModalHandle,
    NothingHere,
    TableHeadOptions,
    SubTopBar,
    Title,
    toArray,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { DropdownItem, DropdownMenu, DropdownToggle, Input, UncontrolledButtonDropdown } from 'reactstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { CreateOrEditProductionProject } from './CreateOrEditProductionProject';
import { ProductionProjectItem } from './ProductionProjectItem';
import { ProductionProjectsStore } from './ProductionProjectsStore';

export interface IProductionProjectListProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class ProductionProjectList extends React.Component<IProductionProjectListProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.productionProjectsStore.loadAllAsync();
            await this.accountsStore.loadAllAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="container-fluid">
                <Title>Production Projects</Title>

                <SubTopBar>
                    <button className="btn btn-primary" onClick={this.modalHandle.open}>
                        Create Project
                    </button>

                    <div className="mx-2 border" />

                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        className="mw-450"
                        placeholder="Search Projects..."
                    />
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <table className="table table-striped w-100 h-100">
                            <thead>
                                <tr>
                                    <th>
                                        Name
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="name"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th>
                                        Account
                                        <TableHeadOptions
                                            value={this.sort}
                                            field="accountName"
                                            onChange={this.onSortChange}
                                            exclusive
                                        />
                                    </th>
                                    <th>
                                        Status
                                        <UncontrolledButtonDropdown>
                                            <DropdownToggle className="p-0 ps-1" caret>
                                                (
                                                {this.statusFilter != undefined
                                                    ? Enums.toString(Enums.ProjectStatus, this.statusFilter)
                                                    : 'All'}
                                                )
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem onClick={() => this.onStatusFilterChange()}>
                                                    All
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        this.onStatusFilterChange(
                                                            Mri.ProductionProjects.ProjectStatus.Active
                                                        )
                                                    }
                                                >
                                                    Active
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        this.onStatusFilterChange(
                                                            Mri.ProductionProjects.ProjectStatus.Completed
                                                        )
                                                    }
                                                >
                                                    Completed
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        this.onStatusFilterChange(
                                                            Mri.ProductionProjects.ProjectStatus.Planning
                                                        )
                                                    }
                                                >
                                                    Planning
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        this.onStatusFilterChange(
                                                            Mri.ProductionProjects.ProjectStatus.ProductionPending
                                                        )
                                                    }
                                                >
                                                    Production Pending
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() =>
                                                        this.onStatusFilterChange(
                                                            Mri.ProductionProjects.ProjectStatus.ToBeDeleted
                                                        )
                                                    }
                                                >
                                                    To Be Deleted
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </UncontrolledButtonDropdown>
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.productionProjects.map(m => (
                                    <ProductionProjectItem
                                        history={this.props.history}
                                        productionProject={m}
                                        key={m.productionProjectGuid}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <NothingHere
                        empty={this.productionProjects.length <= 0}
                        icon={<BsCardHeading />}
                        loading={this.loading}
                    />
                </div>

                <CreateOrEditProductionProject
                    history={this.props.history}
                    modalHandle={this.modalHandle}
                    viewState="create"
                />
            </div>
        );
    }

    @bind
    @action
    private async onStatusFilterChange(status?: Mri.ProductionProjects.ProjectStatus) {
        this.statusFilter = status;
    }

    @bind
    @action
    private async onSortChange(value: string[]) {
        this.sort = value;
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get productionProjects() {
        let projects = this.accountProjects;

        if (this.searchValue) {
            projects = projects!.filter(c => {
                const s = {
                    config: c,
                };

                return deepStringFind(s, this.searchValue);
            });
        }

        if (this.sort.length) {
            const orders = this.sort.map(s => s.split(' '));
            const keys = orders.map(s => s[0]);
            const directions = orders.map(s => (s.length > 1 && s[1] == 'desc' ? 'desc' : 'asc'));
            projects = orderBy(projects, keys, directions);
        }
        if (this.statusFilter != undefined) {
            projects = projects.filter(p => {
                return p.projectStatus === this.statusFilter;
            });
        }
        return projects;
    }

    @computed
    private get accountProjects() {
        return toArray(this.productionProjectsStore.productionProjects).map(p =>
            Object.assign(p, { accountName: this.accountsStore.accounts.get(p.accountNumber)?.accountName })
        );
    }

    private modalHandle = new ModalHandle();
    @inject
    private readonly accountsStore!: AccountsStore;
    @inject
    private readonly productionProjectsStore!: ProductionProjectsStore;

    @observable private sort: string[] = [];
    @observable private searchValue: string = '';
    @observable private statusFilter?: Mri.ProductionProjects.ProjectStatus = 2;
    @observable private loading = false;
}
